import React from 'react';
import { motion } from 'framer-motion';
import { Video, ExternalLink, CheckCircle2 } from 'lucide-react';

const ProductDemoSection = ({ company }) => {
  if (!company.productDemo?.videoUrl && !company.productDemo?.demoLink && (!company.productDemo?.features || company.productDemo.features.length === 0)) {
    return null;
  }

  const renderVideoPlayer = () => {
    if (!company.productDemo?.videoUrl) return null;

    const url = company.productDemo.videoUrl.toLowerCase();
    
    // Check if it's an external video service
    const isExternalVideo = url.includes('youtube.com') || 
                          url.includes('youtu.be') || 
                          url.includes('vimeo.com') || 
                          url.includes('loom.com');
    
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      try {
        const videoUrl = new URL(company.productDemo.videoUrl);
        let embedUrl;
        if (videoUrl.hostname === 'youtu.be') {
          embedUrl = `https://www.youtube.com/embed/${videoUrl.pathname.slice(1)}`;
        } else {
          const videoId = videoUrl.searchParams.get('v');
          embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : company.productDemo.videoUrl;
        }
        return (
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Video player"
            frameBorder="0"
          />
        );
      } catch (e) {
        console.error('Invalid YouTube URL:', e);
      }
    } else if (url.includes('loom.com')) {
      // Handle Loom videos
      const loomId = company.productDemo.videoUrl.split('/').pop();
      return (
        <iframe
          src={`https://www.loom.com/embed/${loomId}`}
          className="w-full h-full"
          allowFullScreen
          title="Loom video"
          frameBorder="0"
        />
      );
    } else if (url.includes('vimeo.com')) {
      // Handle Vimeo videos
      const vimeoId = company.productDemo.videoUrl.split('/').pop();
      return (
        <iframe
          src={`https://player.vimeo.com/video/${vimeoId}`}
          className="w-full h-full"
          allowFullScreen
          title="Vimeo video"
          frameBorder="0"
        />
      );
    }
    // Default to HTML5 video player for Firebase Storage URLs and other direct video files
    return (
      <video 
        src={company.productDemo.videoUrl} 
        controls 
        className="w-full h-full"
        playsInline
        preload="metadata"
      >
        Your browser does not support the video tag.
      </video>
    );
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <Video className="w-6 h-6 text-purple-500" /> Product Demo
      </h2>
      {company.productDemo?.videoUrl && (
        <div className="aspect-video bg-black rounded-2xl overflow-hidden mb-6">
          {renderVideoPlayer()}
        </div>
      )}
      {company.productDemo?.demoLink && (
        <a
          href={company.productDemo.demoLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all mb-4"
        >
          <ExternalLink className="w-5 h-5" /> Try Live Demo
        </a>
      )}
      {company.productDemo?.features?.length > 0 && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {company.productDemo.features.map((feature, index) => (
            <div key={index} className="flex items-center gap-3 text-gray-700">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              {feature}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default ProductDemoSection; 